import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  MessageSquare,
  Mail,
  Phone,
  User,
  Calendar,
  Check,
  Eye,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

export default function ContactosPage() {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContacto, setSelectedContacto] = useState(null);

  useEffect(() => {
    fetchContactos();
  }, []);

  const fetchContactos = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setContactos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar contactos");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    try {
      await axios.put(`${API}/contact/${contactId}/mark-read`);
      setContactos(contactos.map(c => 
        c.id === contactId ? { ...c, lido: true } : c
      ));
      if (selectedContacto?.id === contactId) {
        setSelectedContacto({ ...selectedContacto, lido: true });
      }
      toast.success("Contacto marcado como lido");
    } catch (error) {
      toast.error("Erro ao marcar como lido");
    }
  };

  const deleteContacto = async (contactId) => {
    if (!window.confirm("Tem a certeza que pretende eliminar este contacto?")) return;
    try {
      await axios.delete(`${API}/contact/${contactId}`);
      setContactos(contactos.filter(c => c.id !== contactId));
      if (selectedContacto?.id === contactId) {
        setSelectedContacto(null);
      }
      toast.success("Contacto eliminado");
    } catch (error) {
      toast.error("Erro ao eliminar contacto");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const unreadCount = contactos.filter(c => !c.lido).length;

  return (
    <PageLayout 
      title="Pedidos de Contacto" 
      subtitle={`${unreadCount} pedido${unreadCount !== 1 ? 's' : ''} por ler`}
    >
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : contactos.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B]">Nenhum pedido de contacto recebido</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Lista de contactos */}
          <div className="lg:col-span-1 space-y-3">
            {contactos.map((contacto) => (
              <Card 
                key={contacto.id}
                className={`border-[#E2E8F0] cursor-pointer transition-all hover:border-[#0047FF] ${
                  selectedContacto?.id === contacto.id ? 'border-[#0047FF] ring-1 ring-[#0047FF]' : ''
                } ${!contacto.lido ? 'bg-[#0047FF]/5' : ''}`}
                onClick={() => setSelectedContacto(contacto)}
                data-testid={`contacto-card-${contacto.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-[#0F172A] truncate">{contacto.nome}</p>
                        {!contacto.lido && (
                          <Badge className="bg-[#0047FF] text-xs">Novo</Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#64748B] truncate">{contacto.email}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        {formatDateTime(contacto.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detalhe do contacto */}
          <div className="lg:col-span-2">
            {selectedContacto ? (
              <Card className="border-[#E2E8F0]">
                <CardHeader className="border-b border-[#E2E8F0]">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0F172A]">
                      Detalhes do Contacto
                    </CardTitle>
                    {!selectedContacto.lido && (
                      <Button 
                        onClick={() => markAsRead(selectedContacto.id)}
                        size="sm"
                        className="bg-[#00C853] hover:bg-[#00C853]/90"
                        data-testid="mark-read-btn"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Marcar como Lido
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-[#0047FF]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Nome</p>
                        <p className="font-medium text-[#0F172A]">{selectedContacto.nome}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="w-10 h-10 bg-[#00C853]/10 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#00C853]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Email</p>
                        <a href={`mailto:${selectedContacto.email}`} className="font-medium text-[#0047FF] hover:underline">
                          {selectedContacto.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="w-10 h-10 bg-[#FF9800]/10 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#FF9800]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Contacto</p>
                        <a href={`tel:${selectedContacto.contacto}`} className="font-medium text-[#0047FF] hover:underline">
                          {selectedContacto.contacto}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="w-10 h-10 bg-[#9C27B0]/10 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-[#9C27B0]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B]">Data</p>
                        <p className="font-medium text-[#0F172A]">{formatDateTime(selectedContacto.created_at)}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-[#64748B] mb-3">Mensagem</p>
                    <div className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                      <p className="text-[#0F172A] whitespace-pre-wrap">{selectedContacto.mensagem}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-[#E2E8F0]">
                    <Badge variant={selectedContacto.lido ? "secondary" : "default"} className={selectedContacto.lido ? "" : "bg-[#0047FF]"}>
                      {selectedContacto.lido ? "Lido" : "Não Lido"}
                    </Badge>
                    <div className="flex-1" />
                    <Button 
                      onClick={() => deleteContacto(selectedContacto.id)}
                      size="sm"
                      variant="outline"
                      className="border-[#D50000] text-[#D50000] hover:bg-[#D50000]/10"
                      data-testid="delete-contacto-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <Eye className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#64748B]">Selecione um contacto para ver os detalhes</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
